import {
  AlertCircle,
  ArrowDown,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock3,
  History,
  Inbox,
  LoaderCircle,
  MessageSquare,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  Search,
  Send,
  Star,
  Target,
  Trash2,
  UserRound,
  X,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  deleteOrganizationCalendarScheduleHistory,
  getOrganizationCalendarMyWorkspace,
  getOrganizationCalendarScheduleHistory,
  respondOrganizationCalendarTaskRequest,
  updateOrganizationCalendarSchedule,
} from "lib/api/organizationCalendar";
import { requestGlobalNotificationsRefresh } from "lib/notifications/globalNotifications";

const STATUS_OPTIONS = [
  { value: "pending", label: "대기" },
  { value: "in_progress", label: "진행 중" },
  { value: "completed", label: "완료" },
  { value: "ended", label: "종료" },
  { value: "interrupted", label: "중단" },
];

const REQUEST_STATUS_LABELS = {
  accepted: "수락됨",
  pending: "수락 대기",
  rejected: "반려됨",
};

const TERMINAL_STATUSES = new Set(["completed", "ended", "interrupted"]);

function normalizeId(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

function isSameId(left, right) {
  return Boolean(normalizeId(left)) && normalizeId(left) === normalizeId(right);
}

function normalizeProgress(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  return Math.min(100, Math.max(0, Math.round(number)));
}

function normalizePriority(value) {
  const legacyPriority = {
    low: 2,
    normal: 3,
    high: 4,
    urgent: 5,
  };
  const number = legacyPriority[value] ?? Number(value);
  return String(Math.min(5, Math.max(1, Number.isFinite(number) ? number : 3)));
}

function toDateKey(value) {
  const match = String(value ?? "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : "";
}

function getTodayKey() {
  const parts = new Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    month: "2-digit",
    timeZone: "Asia/Seoul",
    year: "numeric",
  }).formatToParts(new Date());
  const byType = Object.fromEntries(
    parts.filter((part) => part.type !== "literal").map((part) => [
      part.type,
      part.value,
    ]),
  );
  return `${byType.year}-${byType.month}-${byType.day}`;
}

function formatDate(value) {
  const dateKey = toDateKey(value);
  if (!dateKey) return "-";
  const [, month, day] = dateKey.split("-");
  return `${Number(month)}월 ${Number(day)}일`;
}

function formatDateRange(schedule) {
  const start = toDateKey(schedule?.startAt);
  const end = toDateKey(schedule?.endAt);
  if (!start && !end) return "기간 미정";
  if (!start) return `시작일 미정 — ${formatDate(end)}`;
  if (!end) return `${formatDate(start)} — 마감일 미정`;
  if (start === end) return formatDate(start);
  return `${formatDate(start)} — ${formatDate(end)}`;
}

function formatHistoryDateTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("ko-KR", {
    day: "numeric",
    hour: "2-digit",
    hour12: false,
    minute: "2-digit",
    month: "short",
    timeZone: "Asia/Seoul",
    year: "numeric",
  }).format(date);
}

function getStatusLabel(value) {
  return (
    STATUS_OPTIONS.find((option) => option.value === value)?.label ?? "진행 중"
  );
}

function formatHistoryDescription(value) {
  const text = String(value ?? "").trim();
  return text || "등록된 작업 내용이 없습니다.";
}

function buildDescriptionHistoryVersions(
  items,
  currentDescription,
  scheduleCreatedAt,
) {
  const versions = [];
  const chronologicalItems = [...items].reverse();

  chronologicalItems.forEach((item) => {
    if (!item?.changedFields?.includes("description")) return;

    if (
      versions.length === 0 &&
      item.changeType !== "created" &&
      Object.prototype.hasOwnProperty.call(
        item.beforeValues ?? {},
        "description",
      )
    ) {
      versions.push({
        canDelete: item.canDeleteBefore === true,
        content: String(item.beforeValues.description ?? ""),
        createdAt: scheduleCreatedAt || item.createdAt,
        id: `${item.id}-before`,
        historyId: item.id,
        historySource: "before",
      });
    }

    if (
      Object.prototype.hasOwnProperty.call(
        item.afterValues ?? {},
        "description",
      )
    ) {
      versions.push({
        canDelete: item.canDelete === true,
        changedByName: item.changedByName,
        content: String(item.afterValues.description ?? ""),
        createdAt: item.createdAt,
        id: item.id,
        historyId: item.id,
        historySource: "after",
      });
    }
  });

  const currentValue = String(currentDescription ?? "");
  if (
    versions.length > 0 &&
    versions[versions.length - 1].content === currentValue
  ) {
    versions.pop();
  }

  return versions;
}

function isTerminalSchedule(schedule) {
  return TERMINAL_STATUSES.has(schedule?.status);
}

function getUpdatedTime(schedule) {
  const parsed = Date.parse(schedule?.updatedAt ?? schedule?.createdAt ?? "");
  return Number.isFinite(parsed) ? parsed : 0;
}

function getEndTime(schedule) {
  const dateKey = toDateKey(schedule?.endAt);
  const parsed = dateKey ? Date.parse(`${dateKey}T00:00:00+09:00`) : NaN;
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
}

function sortFocusSchedules(left, right) {
  const priorityDifference =
    Number(normalizePriority(right.priority)) -
    Number(normalizePriority(left.priority));
  if (priorityDifference !== 0) return priorityDifference;
  return getEndTime(left) - getEndTime(right);
}

function buildDraft(schedule) {
  return {
    achievementRate: normalizeProgress(schedule?.achievementRate),
    description: String(schedule?.description ?? ""),
    endAt: toDateKey(schedule?.endAt),
    priority: normalizePriority(schedule?.priority),
    startAt: toDateKey(schedule?.startAt),
    status: schedule?.status ?? "in_progress",
  };
}

function hasDraftChanges(schedule, draft) {
  const initial = buildDraft(schedule);
  return (
    initial.status !== draft.status ||
    initial.priority !== draft.priority ||
    initial.achievementRate !== normalizeProgress(draft.achievementRate) ||
    initial.description !== draft.description ||
    initial.startAt !== draft.startAt ||
    initial.endAt !== draft.endAt
  );
}

function ProgressBar({ value }) {
  const rate = normalizeProgress(value);
  return (
    <span
      aria-label={`진행률 ${rate}%`}
      className="organizationMySchedule__progress"
    >
      <i style={{ width: `${rate}%` }} />
    </span>
  );
}

function RequestBadge({ value }) {
  if (!value || value === "not_required") return null;
  return (
    <span
      className={`organizationMySchedule__requestBadge is-${value}`}
    >
      {REQUEST_STATUS_LABELS[value] ?? value}
    </span>
  );
}

function PriorityStars({ value, disabled, onChange }) {
  const priority = Number(normalizePriority(value));
  return (
    <span
      aria-label={`중요도 ${priority}점`}
      className="organizationMySchedule__priorityStars"
    >
      {[1, 2, 3, 4, 5].map((level) =>
        disabled ? (
          <span className={level <= priority ? "isActive" : ""} key={level}>
            <Star
              aria-hidden="true"
              fill={level <= priority ? "currentColor" : "none"}
              size={16}
            />
          </span>
        ) : (
          <button
            aria-label={`중요도 ${level}점으로 변경`}
            className={level <= priority ? "isActive" : ""}
            key={level}
            onClick={() => onChange?.(String(level))}
            type="button"
          >
            <Star
              aria-hidden="true"
              fill={level <= priority ? "currentColor" : "none"}
              size={16}
            />
          </button>
        ),
      )}
    </span>
  );
}

export function OrganizationScheduleHistory({
  allowDelete = false,
  defaultOpen = false,
  hideWhenEmpty = false,
  notify,
  onHistoryDeleted,
  organizationId,
  schedule,
  showAuthor = false,
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deletingVersionId, setDeletingVersionId] = useState("");
  const requestIdRef = useRef(0);

  const loadHistory = useCallback(async () => {
    if (!schedule?.id) return;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsLoading(true);
    setHasLoaded(false);
    setError("");
    setDeleteError("");

    const result = await getOrganizationCalendarScheduleHistory(schedule.id, {
      organizationId,
    });
    if (requestIdRef.current !== requestId) return;

    setIsLoading(false);
    if (!result.success) {
      setError(result.message || "작업 내용 이력을 불러오지 못했습니다.");
      return;
    }

    setItems(Array.isArray(result.data?.history) ? result.data.history : []);
    setHasLoaded(true);
  }, [organizationId, schedule?.id]);

  useEffect(() => {
    if (!isOpen) return;
    loadHistory();
  }, [isOpen, loadHistory, schedule?.updatedAt]);

  useEffect(
    () => () => {
      requestIdRef.current += 1;
    },
    [],
  );

  const descriptionVersions = useMemo(
    () =>
      buildDescriptionHistoryVersions(
        items,
        schedule?.description,
        schedule?.createdAt,
      ),
    [items, schedule?.createdAt, schedule?.description],
  );
  const historyCountLabel = hasLoaded
    ? `${descriptionVersions.length}건`
    : Number(schedule?.historyCount) > 0
      ? "기록 보기"
      : "기록 없음";

  const deleteHistoryVersion = async (version) => {
    if (
      !allowDelete ||
      !version?.canDelete ||
      !version.historyId ||
      deletingVersionId ||
      !window.confirm(
        "선택한 이력을 삭제하시겠습니까?\n삭제한 이력은 복구할 수 없습니다.",
      )
    ) {
      return;
    }

    setDeletingVersionId(version.id);
    setDeleteError("");
    const result = await deleteOrganizationCalendarScheduleHistory(
      schedule.id,
      version.historyId,
      {
        organizationId,
        source: version.historySource,
      },
    );
    setDeletingVersionId("");

    if (!result.success) {
      const message = result.message || "이력을 삭제하지 못했습니다.";
      setDeleteError(message);
      notify?.(message, false);
      return;
    }

    notify?.(result.message || "선택한 이력이 삭제되었습니다.");
    await loadHistory();
    onHistoryDeleted?.();
  };

  if (hideWhenEmpty && Number(schedule?.historyCount) <= 0) return null;

  return (
    <div className={`organizationMySchedule__history ${isOpen ? "isOpen" : ""}`}>
      <button
        aria-expanded={isOpen}
        className="organizationMySchedule__historyToggle"
        onClick={() => setIsOpen((current) => !current)}
        type="button"
      >
        <span>
          <History aria-hidden="true" size={16} />
          <strong>이력</strong>
          <small>{historyCountLabel}</small>
        </span>
        <ChevronDown aria-hidden="true" size={17} />
      </button>

      {isOpen && (
        <div className="organizationMySchedule__historyPanel">
          {deleteError && (
            <div
              className="organizationMySchedule__historyActionError"
              role="alert"
            >
              <AlertCircle aria-hidden="true" size={15} />
              <span>{deleteError}</span>
            </div>
          )}
          {isLoading ? (
            <div className="organizationMySchedule__historyState">
              <LoaderCircle
                aria-hidden="true"
                className="isSpinning"
                size={18}
              />
              <span>작업 내용 이력을 불러오는 중입니다.</span>
            </div>
          ) : error ? (
            <div className="organizationMySchedule__historyState isError">
              <AlertCircle aria-hidden="true" size={18} />
              <span>{error}</span>
              <button onClick={loadHistory} type="button">
                다시 시도
              </button>
            </div>
          ) : descriptionVersions.length > 0 ? (
            <ol className="organizationMySchedule__historyList">
              {descriptionVersions.map((version, index) => (
                <li key={version.id}>
                  <div className="organizationMySchedule__historyEntry">
                    <div
                      className={`organizationMySchedule__historyEntryHeader ${
                        showAuthor && version.changedByName ? "hasAuthor" : ""
                      }`}
                    >
                      {showAuthor && version.changedByName && (
                        <span className="organizationMySchedule__historyAuthor">
                          <strong>{version.changedByName}</strong>
                          님이 저장한 작업 내용
                        </span>
                      )}
                      <div className="organizationMySchedule__historyEntryActions">
                        <span className="organizationMySchedule__historyDate">
                          {formatHistoryDateTime(version.createdAt)}
                        </span>
                        {allowDelete && version.canDelete && (
                          <button
                            aria-label={`${formatHistoryDateTime(
                              version.createdAt,
                            )} 이력 삭제`}
                            className="organizationMySchedule__historyDeleteButton"
                            disabled={Boolean(deletingVersionId)}
                            onClick={() => deleteHistoryVersion(version)}
                            type="button"
                          >
                            {deletingVersionId === version.id ? (
                              <LoaderCircle
                                aria-hidden="true"
                                className="isSpinning"
                                size={13}
                              />
                            ) : (
                              <Trash2 aria-hidden="true" size={13} />
                            )}
                            삭제
                          </button>
                        )}
                      </div>
                    </div>
                    <span className="organizationMySchedule__historyDescription">
                      {formatHistoryDescription(version.content)}
                    </span>
                  </div>
                  {index < descriptionVersions.length - 1 && (
                    <ArrowDown
                      aria-hidden="true"
                      className="organizationMySchedule__historyFlowArrow"
                      size={16}
                    />
                  )}
                </li>
              ))}
            </ol>
          ) : (
            <div className="organizationMySchedule__historyState">
              <History aria-hidden="true" size={18} />
              <span>아직 이전 작업 내용이 없습니다.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ScheduleCard({
  currentUserId,
  isRejecting,
  isResponding,
  isSaving,
  notify,
  onCancelReject,
  onChangeRejectReason,
  onHistoryDeleted,
  onOpenSchedule,
  onRespond,
  onSave,
  organizationId,
  rejectReason,
  schedule,
  todayKey,
}) {
  const [draft, setDraft] = useState(() => buildDraft(schedule));
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setDraft(buildDraft(schedule));
  }, [schedule]);

  const isTaskRequest = schedule.scheduleType === "task_request";
  const isReceivedRequest =
    isTaskRequest && isSameId(schedule.hostUserId, currentUserId);
  const isSentRequest =
    isTaskRequest && isSameId(schedule.createdBy, currentUserId);
  const isMyPersonalSchedule =
    schedule.scheduleType === "personal" &&
    isSameId(schedule.createdBy, currentUserId);
  const isAcceptedAssignment =
    isReceivedRequest && schedule.requestStatus === "accepted";
  const canQuickEdit =
    schedule.canEdit &&
    !isTerminalSchedule(schedule) &&
    (isMyPersonalSchedule || isAcceptedAssignment);
  const showEditor = canQuickEdit && isEditing;
  const isOverdue =
    !isTerminalSchedule(schedule) &&
    Boolean(toDateKey(schedule.endAt)) &&
    toDateKey(schedule.endAt) < todayKey;
  const isDirty = showEditor && hasDraftChanges(schedule, draft);
  const hasInvalidDateRange =
    Boolean(draft.startAt && draft.endAt) && draft.endAt < draft.startAt;
  const partnerLabel = isReceivedRequest
    ? `요청자 ${schedule.createdByName || "-"}`
    : isSentRequest
      ? `담당자 ${schedule.hostUserName || "-"}`
      : schedule.scheduleScope === "organization"
        ? "조직 일정"
        : "개인 일정";

  const updateDraft = (field, value) => {
    setDraft((current) => ({
      ...current,
      [field]: value,
      ...(field === "status" && value === "completed"
        ? { achievementRate: 100 }
        : {}),
    }));
  };

  const handleEditStart = () => {
    setDraft(buildDraft(schedule));
    setIsEditing(true);
  };

  const handleEditCancel = () => {
    setDraft(buildDraft(schedule));
    setIsEditing(false);
  };

  const handleSave = async () => {
    const isSaved = await onSave(schedule, draft);
    if (isSaved) setIsEditing(false);
  };

  const handleCardClick = (event) => {
    if (
      showEditor ||
      event.defaultPrevented ||
      event.target.closest("button, a, input, select, textarea, label")
    ) {
      return;
    }

    onOpenSchedule?.(schedule);
  };

  return (
    <article
      className={[
        "organizationMySchedule__card",
        `is-priority-${normalizePriority(schedule.priority)}`,
        `is-status-${schedule.status ?? "in_progress"}`,
        isOverdue ? "isOverdue" : "",
        showEditor ? "isEditing" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={handleCardClick}
    >
      <div className="organizationMySchedule__cardHeader">
        <div className="organizationMySchedule__cardHeaderContent">
          <span className="organizationMySchedule__cardBadges">
            <span
              className={`organizationMySchedule__statusBadge is-${schedule.status}`}
            >
              {getStatusLabel(schedule.status)}
            </span>
            <RequestBadge value={schedule.requestStatus} />
            {isOverdue && (
              <span className="organizationMySchedule__overdueBadge">
                기한 지남
              </span>
            )}
          </span>
          <div className="organizationMySchedule__cardTitle">
            <button
              className="organizationMySchedule__cardTitleButton"
              onClick={() => onOpenSchedule?.(schedule)}
              type="button"
            >
              {schedule.title}
            </button>
          </div>
          <span className="organizationMySchedule__cardMeta">
            <span>
              <CalendarClock aria-hidden="true" size={14} />
              {formatDateRange(schedule)}
            </span>
            <span>
              <UserRound aria-hidden="true" size={14} />
              {partnerLabel}
            </span>
          </span>
        </div>

        <div className="organizationMySchedule__cardHeaderAside">
          {showEditor ? (
            <span className="organizationMySchedule__editModeBadge">
              <Pencil aria-hidden="true" size={13} />
              수정 모드
            </span>
          ) : (
            <>
              <PriorityStars value={schedule.priority} disabled />
              {canQuickEdit && (
                <button
                  className="organizationMySchedule__editButton"
                  onClick={handleEditStart}
                  type="button"
                >
                  <Pencil aria-hidden="true" size={14} />
                  수정
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {showEditor ? (
        <div className="organizationMySchedule__quickEditor">
          <div className="organizationMySchedule__quickFields">
            <label>
              <span>진행 상태</span>
              <select
                onChange={(event) =>
                  updateDraft("status", event.target.value)
                }
                value={draft.status}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <div className="organizationMySchedule__priorityField">
              <span>중요도</span>
              <PriorityStars
                onChange={(value) => updateDraft("priority", value)}
                value={draft.priority}
              />
            </div>

            <div
              className={`organizationMySchedule__dateField ${
                hasInvalidDateRange ? "isInvalid" : ""
              }`}
            >
              <span>기간</span>
              <div className="organizationMySchedule__dateInputs">
                <input
                  aria-label="일정 시작일"
                  max={draft.endAt || undefined}
                  onChange={(event) =>
                    updateDraft("startAt", event.target.value)
                  }
                  type="date"
                  value={draft.startAt}
                />
                <span aria-hidden="true">—</span>
                <input
                  aria-label="일정 종료일"
                  min={draft.startAt || undefined}
                  onChange={(event) =>
                    updateDraft("endAt", event.target.value)
                  }
                  type="date"
                  value={draft.endAt}
                />
              </div>
            </div>

            <label className="organizationMySchedule__progressField">
              <span>
                진행률
                <strong>{normalizeProgress(draft.achievementRate)}%</strong>
              </span>
              <input
                aria-label="진행률"
                max="100"
                min="0"
                onChange={(event) =>
                  updateDraft("achievementRate", event.target.value)
                }
                step="5"
                type="range"
                value={normalizeProgress(draft.achievementRate)}
              />
            </label>
          </div>

          <label className="organizationMySchedule__descriptionField">
            <span>현재 작업 내용</span>
            <textarea
              maxLength={3000}
              onChange={(event) =>
                updateDraft("description", event.target.value)
              }
              placeholder="현재 진행 상황이나 다음 작업을 기록해 주세요."
              rows={2}
              value={draft.description}
            />
          </label>

          <footer className="organizationMySchedule__cardFooter">
            <span className={isDirty ? "isChanged" : ""}>
              {hasInvalidDateRange
                ? "마감일은 시작일보다 빠를 수 없습니다."
                : isDirty
                  ? "저장하지 않은 변경사항이 있습니다."
                  : "최신 상태입니다."}
            </span>
            <div className="organizationMySchedule__cardFooterActions">
              <button
                className="organizationMySchedule__resetButton"
                disabled={isSaving}
                onClick={handleEditCancel}
                type="button"
              >
                <X aria-hidden="true" size={15} />
                수정 취소
              </button>
              <button
                className="organizationMySchedule__saveButton"
                disabled={!isDirty || hasInvalidDateRange || isSaving}
                onClick={handleSave}
                type="button"
              >
                {isSaving ? (
                  <LoaderCircle
                    aria-hidden="true"
                    className="isSpinning"
                    size={15}
                  />
                ) : (
                  <Save aria-hidden="true" size={15} />
                )}
                변경 사항 저장
              </button>
            </div>
          </footer>
        </div>
      ) : (
        <div className="organizationMySchedule__readonlyBody">
          {schedule.description ? (
            <p>{schedule.description}</p>
          ) : (
            <p className="isEmpty">등록된 작업 내용이 없습니다.</p>
          )}
          <div>
            <ProgressBar value={schedule.achievementRate} />
            <strong>{normalizeProgress(schedule.achievementRate)}%</strong>
          </div>
        </div>
      )}

      {isReceivedRequest &&
        schedule.requestStatus === "pending" &&
        schedule.canRespond && (
          <div className="organizationMySchedule__requestActions">
            {isRejecting ? (
              <div className="organizationMySchedule__rejectForm">
                <label>
                  <span>반려 이유</span>
                  <textarea
                    autoFocus
                    maxLength={500}
                    onChange={(event) =>
                      onChangeRejectReason(event.target.value)
                    }
                    placeholder="요청자에게 전달할 반려 이유를 입력해 주세요."
                    rows={2}
                    value={rejectReason}
                  />
                </label>
                <div>
                  <button
                    disabled={isResponding}
                    onClick={onCancelReject}
                    type="button"
                  >
                    <X aria-hidden="true" size={14} />
                    취소
                  </button>
                  <button
                    className="isReject"
                    disabled={!rejectReason.trim() || isResponding}
                    onClick={() => onRespond(schedule, "reject")}
                    type="button"
                  >
                    <XCircle aria-hidden="true" size={14} />
                    반려 확정
                  </button>
                </div>
              </div>
            ) : (
              <>
                <span>
                  <Inbox aria-hidden="true" size={15} />
                  새 업무 요청입니다. 내용을 확인하고 응답해 주세요.
                </span>
                <div>
                  <button
                    className="isReject"
                    disabled={isResponding}
                    onClick={() => onChangeRejectReason("")}
                    type="button"
                  >
                    <XCircle aria-hidden="true" size={15} />
                    반려
                  </button>
                  <button
                    className="isAccept"
                    disabled={isResponding}
                    onClick={() => onRespond(schedule, "accept")}
                    type="button"
                  >
                    {isResponding ? (
                      <LoaderCircle
                        aria-hidden="true"
                        className="isSpinning"
                        size={15}
                      />
                    ) : (
                      <Check aria-hidden="true" size={15} />
                    )}
                    수락
                  </button>
                </div>
              </>
            )}
          </div>
        )}

      {schedule.requestStatus === "rejected" && schedule.rejectionReason && (
        <p className="organizationMySchedule__rejectionReason">
          <AlertCircle aria-hidden="true" size={15} />
          <span>
            <strong>반려 이유</strong>
            {schedule.rejectionReason}
          </span>
        </p>
      )}

      <OrganizationScheduleHistory
        allowDelete
        notify={notify}
        onHistoryDeleted={onHistoryDeleted}
        organizationId={organizationId}
        schedule={schedule}
      />

      {Number(schedule.commentCount) > 0 && schedule.latestComment && (
        <div className="organizationMySchedule__comments">
          <span className="organizationMySchedule__commentsIcon">
            <MessageSquare aria-hidden="true" size={15} />
          </span>
          <div className="organizationMySchedule__commentsBody">
            <div className="organizationMySchedule__commentsMeta">
              <strong>{schedule.latestComment.authorName}</strong>
              <span>댓글 {schedule.commentCount}개</span>
            </div>
            <p>{schedule.latestComment.content}</p>
          </div>
        </div>
      )}
    </article>
  );
}

export default function OrganizationMySchedules({
  canCreateSchedule = true,
  currentUser,
  notify,
  onCreateSchedule,
  onDataChanged,
  onOpenSchedule,
  onPendingScheduleApplied,
  organizationId,
  organizationName,
  pendingSchedule,
  refreshKey = 0,
}) {
  const [schedules, setSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeView, setActiveView] = useState("focus");
  const [query, setQuery] = useState("");
  const [savingScheduleId, setSavingScheduleId] = useState("");
  const [respondingScheduleId, setRespondingScheduleId] = useState("");
  const [rejectingScheduleId, setRejectingScheduleId] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const workspaceRequestIdRef = useRef(0);

  const handleNotify = useCallback(
    (message, success = true) => {
      notify?.(message, success);
    },
    [notify],
  );

  const loadWorkspace = useCallback(async () => {
    const requestId = workspaceRequestIdRef.current + 1;
    workspaceRequestIdRef.current = requestId;

    if (!organizationId) {
      setSchedules([]);
      setError("조직 정보를 확인할 수 없습니다.");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError("");
    const result = await getOrganizationCalendarMyWorkspace({
      limit: 500,
      organizationId,
    });
    if (requestId !== workspaceRequestIdRef.current) return;

    setIsLoading(false);

    if (!result.success) {
      setSchedules([]);
      setError(
        result.message || "내 일정과 업무 요청을 불러오지 못했습니다.",
      );
      return;
    }

    setSchedules(
      Array.isArray(result.data?.schedules) ? result.data.schedules : [],
    );
  }, [organizationId]);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace, refreshKey]);

  const handleHistoryDeleted = useCallback(() => {
    void loadWorkspace();
    onDataChanged?.();
  }, [loadWorkspace, onDataChanged]);

  useEffect(() => {
    if (!pendingSchedule?.id) return;

    setSchedules((current) => [
      pendingSchedule,
      ...current.filter(
        (schedule) => !isSameId(schedule.id, pendingSchedule.id),
      ),
    ]);
    setActiveView(
      pendingSchedule.scheduleType === "task_request" ? "sent" : "focus",
    );
    setQuery("");
    setError("");
    onPendingScheduleApplied?.(pendingSchedule.id);
  }, [onPendingScheduleApplied, pendingSchedule]);

  const todayKey = getTodayKey();
  const categorized = useMemo(() => {
    const userId = currentUser?.id;
    const focus = [];
    const received = [];
    const sent = [];
    const history = [];

    schedules.forEach((schedule) => {
      const isTaskRequest = schedule.scheduleType === "task_request";
      const isReceived =
        isTaskRequest && isSameId(schedule.hostUserId, userId);
      const isSent = isTaskRequest && isSameId(schedule.createdBy, userId);
      const isPersonal =
        schedule.scheduleType === "personal" &&
        isSameId(schedule.createdBy, userId);
      const isAcceptedAssignment =
        isReceived && schedule.requestStatus === "accepted";
      const endDateKey = toDateKey(schedule.endAt);
      const isPastSchedule = Boolean(endDateKey) && endDateKey < todayKey;

      if (isReceived) received.push(schedule);
      if (isSent) sent.push(schedule);
      if (isPastSchedule) history.push(schedule);
      if (
        !isPastSchedule &&
        !isTerminalSchedule(schedule) &&
        (isPersonal || isAcceptedAssignment)
      ) {
        focus.push(schedule);
      }
    });

    focus.sort(sortFocusSchedules);
    received.sort((left, right) => getUpdatedTime(right) - getUpdatedTime(left));
    sent.sort((left, right) => getUpdatedTime(right) - getUpdatedTime(left));
    history.sort((left, right) => getEndTime(right) - getEndTime(left));

    return {
      all: [...schedules].sort(
        (left, right) => getUpdatedTime(right) - getUpdatedTime(left),
      ),
      focus,
      history,
      received,
      sent,
    };
  }, [currentUser?.id, schedules, todayKey]);

  const pendingReceivedCount = categorized.received.filter(
    (schedule) => schedule.requestStatus === "pending",
  ).length;
  const pendingSentCount = categorized.sent.filter(
    (schedule) => schedule.requestStatus === "pending",
  ).length;
  const inProgressCount = categorized.focus.filter(
    (schedule) => schedule.status === "in_progress",
  ).length;
  const averageProgress =
    categorized.history.length > 0
      ? Math.round(
          categorized.history.reduce(
            (sum, schedule) =>
              sum + normalizeProgress(schedule.achievementRate),
            0,
          ) / categorized.history.length,
        )
      : 0;
  const overdueCount = categorized.history.length;

  const views = [
    {
      count: categorized.focus.length,
      icon: Target,
      id: "focus",
      label: "진행할 일정",
    },
    {
      count: categorized.received.length,
      icon: Inbox,
      id: "received",
      label: "받은 요청",
    },
    {
      count: categorized.sent.length,
      icon: Send,
      id: "sent",
      label: "보낸 요청",
    },
    {
      count: categorized.history.length,
      icon: History,
      id: "history",
      label: "지난 일정",
    },
    {
      count: categorized.all.length,
      icon: CalendarClock,
      id: "all",
      label: "전체 기록",
    },
  ];

  const visibleSchedules = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase("ko-KR");
    const source = categorized[activeView] ?? [];
    if (!normalizedQuery) return source;

    return source.filter((schedule) =>
      [
        schedule.title,
        schedule.description,
        schedule.createdByName,
        schedule.hostUserName,
        getStatusLabel(schedule.status),
        REQUEST_STATUS_LABELS[schedule.requestStatus],
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLocaleLowerCase("ko-KR").includes(normalizedQuery),
        ),
    );
  }, [activeView, categorized, query]);

  const activeViewLabel =
    views.find((view) => view.id === activeView)?.label ?? "진행할 일정";

  const handleSave = useCallback(
    async (schedule, draft) => {
      if (!schedule?.id) return false;
      if (draft.startAt && draft.endAt && draft.endAt < draft.startAt) {
        handleNotify("마감일은 시작일보다 빠를 수 없습니다.", false);
        return false;
      }

      setSavingScheduleId(schedule.id);
      const result = await updateOrganizationCalendarSchedule(schedule.id, {
        achievementRate: normalizeProgress(draft.achievementRate),
        description: draft.description.trim(),
        endAt: draft.endAt,
        hostUserId:
          schedule.scheduleType === "task_request"
            ? schedule.hostUserId || null
            : null,
        organizationId,
        priority: normalizePriority(draft.priority),
        scheduleScope: schedule.scheduleScope ?? "personal",
        scheduleType: schedule.scheduleType ?? "personal",
        startAt: draft.startAt,
        status: draft.status,
        title: schedule.title,
      });
      setSavingScheduleId("");

      if (!result.success) {
        handleNotify(result.message || "일정을 수정하지 못했습니다.", false);
        return false;
      }

      handleNotify(result.message || "일정이 수정되었습니다.");
      await loadWorkspace();
      onDataChanged?.();
      return true;
    },
    [handleNotify, loadWorkspace, onDataChanged, organizationId],
  );

  const handleRejectStart = useCallback((scheduleId) => {
    setRejectingScheduleId(scheduleId);
    setRejectionReason("");
  }, []);

  const handleRespond = useCallback(
    async (schedule, action) => {
      if (!schedule?.id) return;
      const trimmedReason = rejectionReason.trim();
      if (action === "reject" && !trimmedReason) {
        handleNotify("반려 이유를 입력해 주세요.", false);
        return;
      }

      setRespondingScheduleId(schedule.id);
      const result = await respondOrganizationCalendarTaskRequest(
        schedule.id,
        {
          action,
          rejectionReason: action === "reject" ? trimmedReason : "",
        },
      );
      setRespondingScheduleId("");

      if (!result.success) {
        handleNotify(result.message || "업무 요청을 처리하지 못했습니다.", false);
        return;
      }

      setRejectingScheduleId("");
      setRejectionReason("");
      handleNotify(result.message || "업무 요청을 처리했습니다.");
      requestGlobalNotificationsRefresh();
      await loadWorkspace();
      onDataChanged?.();
    },
    [
      handleNotify,
      loadWorkspace,
      onDataChanged,
      rejectionReason,
    ],
  );

  return (
    <section className="organizationMySchedule">
      <div className="organizationMySchedule__hero">
        <div className="organizationMySchedule__heroContent">
          <span className="organizationMySchedule__eyebrow">
            <Target aria-hidden="true" size={14} />
            {organizationName || "조직"} · MY WORK
          </span>
          <div className="organizationMySchedule__heroHeading">
            <h2>내 일정</h2>
          </div>
        </div>
        <div className="organizationMySchedule__heroActions">
          <button
            aria-label="내 일정 새로고침"
            disabled={isLoading}
            onClick={loadWorkspace}
            title="새로고침"
            type="button"
          >
            <RefreshCw
              aria-hidden="true"
              className={isLoading ? "isSpinning" : ""}
              size={16}
            />
          </button>
          <button
            className="organizationPage__createBtn"
            disabled={!canCreateSchedule}
            onClick={() => onCreateSchedule?.(new Date())}
            title={
              canCreateSchedule
                ? "일정 등록"
                : "내 소속 페이지에서만 일정을 등록할 수 있습니다."
            }
            type="button"
          >
            <Plus aria-hidden="true" size={16} />
            일정 등록
          </button>
        </div>
      </div>

      <div className="organizationMySchedule__stats">
        <article>
          <span className="isBlue">
            <Target aria-hidden="true" size={17} />
          </span>
          <div>
            <small>진행 중</small>
            <strong>{inProgressCount}</strong>
          </div>
          <em>건</em>
        </article>
        <article>
          <span className="isGreen">
            <CheckCircle2 aria-hidden="true" size={17} />
          </span>
          <div>
            <small>평균 진행률</small>
            <strong>{averageProgress}</strong>
          </div>
          <em>%</em>
        </article>
        <article>
          <span className="isOrange">
            <Inbox aria-hidden="true" size={17} />
          </span>
          <div>
            <small>응답할 요청</small>
            <strong>{pendingReceivedCount}</strong>
          </div>
          <em>건</em>
        </article>
        <article>
          <span className="isViolet">
            <Send aria-hidden="true" size={17} />
          </span>
          <div>
            <small>수락 대기</small>
            <strong>{pendingSentCount}</strong>
          </div>
          <em>건</em>
        </article>
        <article className={overdueCount > 0 ? "isWarning" : ""}>
          <span className="isRed">
            <Clock3 aria-hidden="true" size={17} />
          </span>
          <div>
            <small>지난 일정</small>
            <strong>{overdueCount}</strong>
          </div>
          <em>건</em>
        </article>
      </div>

      <div className="organizationMySchedule__workspace">
        <nav aria-label="내 일정 분류" className="organizationMySchedule__nav">
          {views.map((view) => {
            const Icon = view.icon;
            return (
              <button
                aria-current={activeView === view.id ? "page" : undefined}
                className={activeView === view.id ? "isActive" : ""}
                key={view.id}
                onClick={() => setActiveView(view.id)}
                type="button"
              >
                <Icon aria-hidden="true" size={16} />
                <span>{view.label}</span>
                <strong>{view.count}</strong>
              </button>
            );
          })}
        </nav>

        <div className="organizationMySchedule__content">
          <div className="organizationMySchedule__contentHeader">
            <div>
              <h3>{activeViewLabel}</h3>
              <span>{visibleSchedules.length}건</span>
            </div>
            <label className="organizationMySchedule__search">
              <Search aria-hidden="true" size={16} />
              <input
                onChange={(event) => setQuery(event.target.value)}
                placeholder="일정명, 내용, 구성원 검색"
                type="search"
                value={query}
              />
              {query && (
                <button
                  aria-label="검색어 지우기"
                  onClick={() => setQuery("")}
                  type="button"
                >
                  <X aria-hidden="true" size={14} />
                </button>
              )}
            </label>
          </div>

          {error ? null : isLoading ? (
            <div
              aria-live="polite"
              className="organizationMySchedule__state"
            >
              <LoaderCircle
                aria-hidden="true"
                className="isSpinning"
                size={26}
              />
              <strong>내 일정을 정리하고 있습니다.</strong>
            </div>
          ) : visibleSchedules.length > 0 ? (
            <div className="organizationMySchedule__list">
              {visibleSchedules.map((schedule) => (
                <ScheduleCard
                  currentUserId={currentUser?.id}
                  isRejecting={rejectingScheduleId === schedule.id}
                  isResponding={respondingScheduleId === schedule.id}
                  isSaving={savingScheduleId === schedule.id}
                  key={schedule.id}
                  notify={handleNotify}
                  onCancelReject={() => {
                    setRejectingScheduleId("");
                    setRejectionReason("");
                  }}
                  onChangeRejectReason={(value) => {
                    if (rejectingScheduleId !== schedule.id) {
                      handleRejectStart(schedule.id);
                    } else {
                      setRejectionReason(value);
                    }
                  }}
                  onRespond={handleRespond}
                  onHistoryDeleted={handleHistoryDeleted}
                  onOpenSchedule={onOpenSchedule}
                  onSave={handleSave}
                  organizationId={organizationId}
                  rejectReason={rejectionReason}
                  schedule={schedule}
                  todayKey={todayKey}
                />
              ))}
            </div>
          ) : (
            <div className="organizationMySchedule__state">
              {activeView === "history" ? (
                <History aria-hidden="true" size={27} />
              ) : (
                <CheckCircle2 aria-hidden="true" size={27} />
              )}
              <strong>
                {query
                  ? "검색 결과가 없습니다."
                  : activeView === "focus"
                    ? "현재 진행할 일정이 없습니다."
                    : `${activeViewLabel}이 없습니다.`}
              </strong>
              <span>
                {query
                  ? "다른 검색어로 다시 찾아보세요."
                  : activeView === "focus"
                    ? "새 일정을 등록하면 이곳에서 바로 관리할 수 있습니다."
                    : "새로운 기록이 생기면 이곳에 표시됩니다."}
              </span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
